import React, { useEffect, useState, useRef } from 'react';
import {
  Bold,
  Italic,
  Underline,
  List,
  Link as LinkIcon,
  Image,
  RotateCcw,
  RotateCw,
  Type,
  Code,
  Smile,
  Grid,
  Trash2,
} from 'react-feather';
import {
  TEXT_COLOR_PRESETS,
  HIGHLIGHT_PRESETS,
  EMOJI_PRESETS,
  promptForLink,
  getBlockStyleValue,
  applyBlockStyle,
} from './tiptapToolbarUtils';

const StrikeIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M5 12h14" />
    <path d="M7 8c2-2 8-2 10 0M7 16c2 2 8 2 10 0" />
  </svg>
);

export function ToolbarButton({
  onClick,
  isActive = false,
  disabled = false,
  title,
  children,
  className = '',
}) {
  return (
    <button
      type="button"
      className={`sk-tiptap-toolbar-btn${isActive ? ' is-active' : ''} ${className}`.trim()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
    >
      {children}
    </button>
  );
}

export function ToolbarDivider() {
  return <span className="sk-tiptap-toolbar-divider" aria-hidden="true" />;
}

function useToolbarRefresh(editor) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!editor || editor.isDestroyed) return undefined;
    const bump = () => setTick((t) => t + 1);
    editor.on('selectionUpdate', bump);
    editor.on('update', bump);
    return () => {
      editor.off('selectionUpdate', bump);
      editor.off('update', bump);
    };
  }, [editor]);
}

function ColorPopover({ presets, onApply, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="sk-tiptap-popover-wrap" ref={ref}>
      <ToolbarButton title={ariaLabel} onClick={() => setOpen((o) => !o)}>
        <Type size={16} />
      </ToolbarButton>
      {open ? (
        <div className="sk-tiptap-popover sk-tiptap-color-popover" role="menu">
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              className="sk-tiptap-swatch"
              title={p.label}
              style={p.value ? { background: p.value } : undefined}
              onClick={() => {
                onApply(p.value);
                setOpen(false);
              }}
            >
              {!p.value ? '×' : ''}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function HighlightPopover({ editor }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="sk-tiptap-popover-wrap" ref={ref}>
      <ToolbarButton title="Highlight" onClick={() => setOpen((o) => !o)}>
        <span className="sk-tiptap-highlight-icon" aria-hidden="true">A</span>
      </ToolbarButton>
      {open ? (
        <div className="sk-tiptap-popover sk-tiptap-color-popover" role="menu">
          {HIGHLIGHT_PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              className="sk-tiptap-swatch"
              title={p.label}
              style={p.value ? { background: p.value } : undefined}
              onClick={() => {
                if (!p.value) editor.chain().focus().unsetHighlight().run();
                else editor.chain().focus().toggleHighlight({ color: p.value }).run();
                setOpen(false);
              }}
            >
              {!p.value ? '×' : ''}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function EmojiPopover({ editor }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="sk-tiptap-popover-wrap" ref={ref}>
      <ToolbarButton title="Insert emoji" onClick={() => setOpen((o) => !o)}>
        <Smile size={16} />
      </ToolbarButton>
      {open ? (
        <div className="sk-tiptap-popover sk-tiptap-emoji-popover" role="menu">
          {EMOJI_PRESETS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="sk-tiptap-emoji-btn"
              onClick={() => {
                editor.chain().focus().insertContent(emoji).run();
                setOpen(false);
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TipTapToolbar({ editor, onImageClick, imageUploadDisabled = false }) {
  useToolbarRefresh(editor);

  if (!editor || editor.isDestroyed) return null;

  const blockStyle = getBlockStyleValue(editor);

  return (
    <div className="sk-tiptap-toolbar" role="toolbar" aria-label="Formatting">
      <select
        className="sk-tiptap-toolbar-select"
        value={blockStyle}
        onChange={(e) => applyBlockStyle(editor, e.target.value)}
        title="Text style"
        aria-label="Text style"
      >
        <option value="paragraph">Normal text</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
        <option value="h4">Heading 4</option>
      </select>

      <ToolbarDivider />

      <ToolbarButton
        title="Bold"
        isActive={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold size={16} />
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        isActive={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic size={16} />
      </ToolbarButton>
      <ToolbarButton
        title="Underline"
        isActive={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <Underline size={16} />
      </ToolbarButton>
      <ToolbarButton
        title="Strikethrough"
        isActive={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <StrikeIcon />
      </ToolbarButton>

      <ToolbarDivider />

      <ColorPopover
        presets={TEXT_COLOR_PRESETS}
        ariaLabel="Text color"
        onApply={(color) => {
          if (!color) editor.chain().focus().unsetColor().run();
          else editor.chain().focus().setColor(color).run();
        }}
      />
      <HighlightPopover editor={editor} />

      <ToolbarDivider />

      <ToolbarButton
        title="Bullet list"
        isActive={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List size={16} />
      </ToolbarButton>
      <ToolbarButton
        title="Numbered list"
        isActive={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <span className="sk-tiptap-ol-icon" aria-hidden="true">1.</span>
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton
        title="Quote"
        isActive={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <span className="sk-tiptap-quote-icon" aria-hidden="true">&ldquo;</span>
      </ToolbarButton>
      <ToolbarButton
        title="Code block"
        isActive={editor.isActive('codeBlock')}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      >
        <Code size={16} />
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton
        title="Link"
        isActive={editor.isActive('link')}
        onClick={() => promptForLink(editor)}
      >
        <LinkIcon size={16} />
      </ToolbarButton>
      <ToolbarButton
        title={imageUploadDisabled ? 'Save the product first to upload images' : 'Image'}
        disabled={imageUploadDisabled}
        onClick={onImageClick}
      >
        <Image size={16} />
      </ToolbarButton>
      <ToolbarButton
        title="Insert table"
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
      >
        <Grid size={16} />
      </ToolbarButton>
      <EmojiPopover editor={editor} />

      <ToolbarDivider />

      <ToolbarButton
        title="Undo"
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <RotateCcw size={16} />
      </ToolbarButton>
      <ToolbarButton
        title="Redo"
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <RotateCw size={16} />
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton
        title="Clear formatting"
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
      >
        <Trash2 size={16} />
      </ToolbarButton>
    </div>
  );
}

export default TipTapToolbar;
